/**
 * Datos de Horarios › Ausencias (008 · T076 · US4; contracts/admin-api.md A21–A24).
 *
 * - `GET /absences?from_day&to_day&worker_id&site_id&type&status&cursor` (paginado por cursor).
 * - `POST /absences` (`schedules.write`): solape de día completo con otra activa ⇒ `400
 *   VALIDATION_ERROR` en `start_day` con el texto del rango ("Se cruza con otra ausencia del 5 al
 *   16 de octubre"); día de un período cerrado ⇒ `409 PERIOD_CLOSED`.
 * - `POST /absences/{id}/cancel` con motivo 1–300; una de integración ⇒ `409 INTEGRATION_MANAGED`.
 *
 * Toda respuesta se valida con el esquema Zod de `@fermin/contracts`. Sin optimistic UI: registrar
 * o cancelar invalida la lista, el cuadro (icono de ausencia en la celda) y el tareo.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';

import {
  AbsenceListResponseSchema,
  AbsenceSchema,
  type Absence,
  type AbsenceListResponse,
  type AbsenceType,
  type CancelAbsenceBody,
  type CreateAbsenceBody,
} from '@fermin/contracts';

import { api, type ApiError } from '../../api/client';
import { useInvalidatePlanViews } from '../schedules/api';

export const ABSENCES_QUERY_KEY = ['absences'] as const;

export type AbsenceStatusFilter = 'ACTIVE' | 'CANCELLED' | 'ALL';

/** Filtros aplicados de ausencias (`AbsenceListQuery` sin `cursor`/`limit`). */
export interface AbsenceFilters {
  from_day?: string;
  to_day?: string;
  worker_id?: string;
  site_id?: string;
  type?: AbsenceType | '';
  status?: AbsenceStatusFilter;
}

const FILTER_KEYS = ['from_day', 'to_day', 'worker_id', 'site_id', 'type', 'status'] as const;

/** `GET /absences?…&cursor` (se omiten los filtros vacíos). */
export function absencesPath(filters: AbsenceFilters, cursor?: string): string {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = filters[key]?.trim();
    if (value) {
      params.set(key, value);
    }
  }
  if (cursor) {
    params.set('cursor', cursor);
  }
  const query = params.toString();
  return query ? `/absences?${query}` : '/absences';
}

/** Clave exacta de las páginas (la que recorta `RefreshButton`). */
export function absencesKey(filters: AbsenceFilters | null): unknown[] {
  return [...ABSENCES_QUERY_KEY, 'page', filters];
}

/** Lista paginada ("Cargar más"). `filters = null` no consulta (rango inválido). */
export function useAbsences(
  filters: AbsenceFilters | null,
): UseInfiniteQueryResult<{ pages: AbsenceListResponse[] }, ApiError> {
  return useInfiniteQuery<
    AbsenceListResponse,
    ApiError,
    { pages: AbsenceListResponse[] },
    unknown[],
    string | null
  >({
    queryKey: absencesKey(filters),
    queryFn: ({ pageParam }) =>
      api.get(absencesPath(filters!, pageParam ?? undefined), AbsenceListResponseSchema),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    enabled: filters !== null,
  });
}

function useInvalidateAbsences(): () => Promise<void> {
  const queryClient = useQueryClient();
  const invalidatePlan = useInvalidatePlanViews();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ABSENCES_QUERY_KEY }),
      invalidatePlan(),
    ]);
  };
}

/** `POST /absences` → `201 Absence`. */
export function useCreateAbsence() {
  const invalidate = useInvalidateAbsences();
  return useMutation<Absence, ApiError, CreateAbsenceBody>({
    mutationFn: (body) => api.post('/absences', body, AbsenceSchema),
    onSuccess: invalidate,
  });
}

export interface CancelAbsenceVars {
  id: string;
  body: CancelAbsenceBody;
}

/** `POST /absences/{id}/cancel` → `200 Absence` (queda en el historial como cancelada). */
export function useCancelAbsence() {
  const invalidate = useInvalidateAbsences();
  return useMutation<Absence, ApiError, CancelAbsenceVars>({
    mutationFn: ({ id, body }) => api.post(`/absences/${id}/cancel`, body, AbsenceSchema),
    onSuccess: invalidate,
  });
}
